import React, { useState } from 'react'
import Box from '@mui/material/Box'
import useMediaQuery from '@mui/material/useMediaQuery'
import { alpha, useTheme } from '@mui/material/styles'
import CandleLoader from './CandleLoader'
import useIntroAnimation from '../hooks/useIntroAnimation'
import {
  brandingForTradingHouse,
  lastKnownTradingHouse,
} from '../common/utils/tradingHouseBranding'

const GLOW_SIZE = 560
const ICON_WIDTH = 90
const WORDMARK_WIDTH = 180

export default function LoadingShell() {
  const theme = useTheme()
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const playIntro = useIntroAnimation('loadingShell') && !prefersReducedMotion

  // Resolved once per mount: this paints before usermeta loads, so the house
  // comes from the cached hint rather than the application suite context.
  const [branding] = useState(() => brandingForTradingHouse(lastKnownTradingHouse()))

  const rise = (delay: string) => ({
    '@keyframes loadingShellRise': {
      from: { opacity: 0, transform: 'translateY(6px) scale(0.97)' },
      to: { opacity: 1, transform: 'translateY(0) scale(1)' },
    },
    opacity: playIntro ? 0 : 1,
    animation: playIntro ? `loadingShellRise 0.6s ease-out ${delay} forwards` : 'none',
  })

  return (
    <Box
      className="mainLayout"
      sx={{
        backgroundColor: theme.palette.background.custom1,
        position: 'relative',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3.5,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          width: GLOW_SIZE,
          height: GLOW_SIZE,
          maxWidth: '90vw',
          maxHeight: '90vw',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(
            theme.palette.primary.main,
            0.22
          )} 0%, ${alpha(theme.palette.primary.main, 0)} 70%)`,
          '@keyframes loadingShellBreathe': {
            '0%, 100%': { transform: 'scale(0.92)', opacity: 0.7 },
            '50%': { transform: 'scale(1.05)', opacity: 1 },
          },
          animation: prefersReducedMotion
            ? 'none'
            : 'loadingShellBreathe 3.2s ease-in-out infinite',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2.75,
          ...rise('0.1s'),
        }}
      >
        <Box
          component="img"
          src={branding.icon}
          alt=""
          sx={{ width: ICON_WIDTH, maxWidth: '18vw', height: 'auto' }}
        />
        <Box
          component="img"
          src={branding.wordmark}
          alt={branding.name}
          sx={{ width: WORDMARK_WIDTH, maxWidth: '28vw', height: 'auto' }}
        />
      </Box>

      <Box sx={{ position: 'relative' }}>
        <CandleLoader />
      </Box>
    </Box>
  )
}
